import React, { useEffect } from "react";
import { connect } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { Container } from "reactstrap";

import { Api } from "@api/index";
import { connectToApi, ApiConnectedOwnProps } from "@api/components/connectToApi";
import { setTitle } from "@app/util";
import { messages } from "@app/messages";
import { GlobalState } from "@app/models/state";
import { Track } from "@music/models";
import { PropsWithId, makeGetAlbum, canViewTrack as canViewTrackSelector } from "@music/selectors";
import { Album as AlbumEl } from "@music/components/Album";
import { messages as mMessages } from "@music/messages";
import {
    canViewSales as canViewSalesSelector,
    canViewStreamDemographics as canViewStreamDemographicsSelector,
    canViewPlaylistTrends as canViewPlaylistTrendsSelector,
 } from "@sales/selectors";
import { DemographicsCharts } from "@sales/components/DemographicsCharts";
import { GeoSalesChart } from "@sales/components/GeoSalesChart";
import { TrackSalesTable } from "@sales/components/TrackSalesTable";
import { Sales } from "@sales/components/Sales";
import { PlaylistTrendsList } from "@sales/components/PlaylistTrendsList";
import { messages as sMessages } from "@sales/messages";
import { BreadcrumbsItem } from "@ui/Breadcrumbs";
import { Page, PageTitle } from "@ui/Page";
import { compose } from "@util/compose";
import { albumNotFound, HasAlbum } from "@app/components/notFoundHocs";

export interface AlbumsViewProps extends HasAlbum, ApiConnectedOwnProps {
    canViewSales: boolean;
    canViewTrack: boolean;
    canViewStreamDemographics: boolean;
    canViewPlaylistTrends: boolean;
}

const AlbumsView = ({
    album,
    canViewSales,
    canViewStreamDemographics,
    canViewTrack,
    canViewPlaylistTrends,
}: AlbumsViewProps) => {
    const intl = useIntl();
    useEffect(() => {
        setTitle(intl, messages.pageAlbumsTitle, album.title);
    }, [album]);

    const breadcrumbItems: BreadcrumbsItem[] = [{ text: album.title}];
    return (
        <Page className="albums-view-page" breadcrumbs={breadcrumbItems} fluid>
            <div className="albums-view-page__details">
                <AlbumEl
                    album={album}
                    link={false}
                    style="headline"
                    additionalDetails={true}
                />
            </div>

            <Container>
                {album.commentPrivate != null && album.commentPrivate !== "" &&
                    <>
                        <PageTitle className="m-0" tag="h5">
                            <FormattedMessage {...mMessages.albumLinks} />
                        </PageTitle>
                        <p><em><FormattedMessage {...mMessages.albumLinksDescription} /></em></p>
                        <div
                            className="albums-view-page__links"
                            dangerouslySetInnerHTML={{__html: album.commentPrivate}}
                        ></div>
                    </>
                }

                {canViewSales && <Sales albumId={album.id} />}

                {canViewTrack &&
                    <PageTitle className="display-3 mb-5" tag="h5">
                        <FormattedMessage {...mMessages.trackEntityPlural} />
                    </PageTitle>}
                {canViewTrack &&
                    <TrackSalesTable albumId={album.id}
                        tracks={album.tracks as Track[]}
                    />}
                {canViewSales && <GeoSalesChart albumId={album.id} />}
                {canViewPlaylistTrends && (
                    <>
                        <PageTitle className="display-3 mb-5" tag="h5">
                            <FormattedMessage {...sMessages.playlistTrendsTitle} />
                        </PageTitle>
                        <PlaylistTrendsList showTracks albumId={album.id} />
                    </>)
                    }
                {canViewStreamDemographics &&
                    <DemographicsCharts albumId={album.id} />
                }
            </Container>
        </Page>
    );
};

// Map the users state to the component.
const makeMapStateToProps = () => {
    const getAlbum = makeGetAlbum();
    return (state: GlobalState, ownProps: PropsWithId) => {
        const album = getAlbum(state, ownProps);
        return {
            album,
            lastLoaded: [ album ? album.lastLoaded : null ],
            canViewSales: canViewSalesSelector(state),
            canViewTrack: canViewTrackSelector(state),
            canViewStreamDemographics: canViewStreamDemographicsSelector(state),
            canViewPlaylistTrends: canViewPlaylistTrendsSelector(state),
        } as AlbumsViewProps;
    };
};

const makeAlbumsShow = (id: number) => () => {
    return Api.albumsShow(id);
};

const ConnectedAlbumsView = compose<AlbumsViewProps, PropsWithId>(
    connect(makeMapStateToProps, null),
    connectToApi((props: PropsWithId) => {
        return {
            actionCreators: [ makeAlbumsShow(Number(props.params.id)) ],
            showRefresh: false,
        };
    }),
    albumNotFound,
)(AlbumsView);

export {
    AlbumsView as AlbumsViewPresenter,
    ConnectedAlbumsView as AlbumsView,
};
